import React, { useState, useEffect, useRef } from "react";
import Layout from "@/components/utu/Layout";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ChevronRight,
  ArrowRight,
  Cpu,
  Database,
  Layers,
  Server,
  Shield,
  Clock,
  Compass,
  Check,
  Play,
  Zap,
  Globe2,
  X,
  BookOpen,
} from "lucide-react";
import {
  GoldPillButton,
  OutlinePillButton,
  GlassCard,
  scrollToId,
} from "@/components/utu/atoms";
import { FingerprintOrb, BrandMark } from "@/components/utu/visuals";
import {
  SERVICE_CARDS,
  CASE_STUDIES,
  TECH_STACK,
  BOOKING_URL,
  CONTACT_EMAIL,
  INSIGHTS_ARTICLES,
  ENGAGEMENT_TIERS,
} from "@/mock";
import { submitContact, submitWaitlist } from "@/utils/api";
import { SEO, websiteStructuredData, organizationStructuredData } from "@/components/SEO";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const serviceIcons = {
  "custom-ai-models": Database,
  "utu-mcp-server": Cpu,
  "custom-software": Layers,
  "research-rnd": Compass,
};

export default function UtuLanding() {
  const [lead, setLead] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  // Waitlist state
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistLead, setWaitlistLead] = useState({ name: "", email: "", role: "", use_case: "" });
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);

  // Active Case Study Tab
  const [activeCaseStudyId, setActiveCaseStudyId] = useState(CASE_STUDIES[0].id);

  // Interactive Pipeline step simulation
  const [pipelineStep, setPipelineStep] = useState(0);

  useEffect(() => {
    // Auto increment interactive pipeline steps
    const interval = setInterval(() => {
      setPipelineStep((prev) => (prev + 1) % 4);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // GSAP Hero Animation
    const tl = gsap.timeline({ defaults: { ease: "power3.out", duration: 1 } });
    tl.fromTo(".gsap-hero-badge", { opacity: 0, y: 15 }, { opacity: 1, y: 0 })
      .fromTo(".gsap-hero-title", { opacity: 0, y: 30 }, { opacity: 1, y: 0 }, "-=0.7")
      .fromTo(".gsap-hero-desc", { opacity: 0, y: 20 }, { opacity: 1, y: 0 }, "-=0.6")
      .fromTo(".gsap-hero-cta", { opacity: 0, y: 15 }, { opacity: 1, y: 0, stagger: 0.1 }, "-=0.5")
      .fromTo(".gsap-hero-orb", { opacity: 0, scale: 0.8 }, { opacity: 1, scale: 1 }, "-=0.8");

    // GSAP Scroll Trigger Animations
    const reveals = document.querySelectorAll(".gsap-reveal");
    reveals.forEach((el) => {
      gsap.fromTo(
        el,
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: el,
            start: "top 85%",
            toggleActions: "play none none none",
          },
        }
      );
    });

    return () => {
      ScrollTrigger.getAll().forEach((t) => t.kill());
    };
  }, []);

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.email || !lead.email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      await submitContact({
        name: lead.name.trim(),
        email: lead.email.trim().toLowerCase(),
        company: lead.company.trim(),
        message: lead.message.trim(),
      });
      toast.success("Message sent — we will respond within 24 hours.");
      setLead({ name: "", email: "", company: "", message: "" });
    } catch (error) {
      toast.error(error.message || "Failed to send message.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!waitlistLead.email || !waitlistLead.email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setWaitlistSubmitting(true);
    try {
      await submitWaitlist({
        name: waitlistLead.name.trim(),
        email: waitlistLead.email.trim().toLowerCase(),
        role: waitlistLead.role.trim(),
        use_case: waitlistLead.use_case.trim(),
      });
      toast.success("Joined waitlist — check your inbox for instructions.");
      setWaitlistLead({ name: "", email: "", role: "", use_case: "" });
      setWaitlistOpen(false);
    } catch (error) {
      toast.error(error.message || "Failed to join waitlist.");
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO
        title="AI & Technology Lab | Sovereign Local Models & Context Architecture"
        description="UTU Africa builds custom local AI models, high-performance software systems, and the UTU MCP Server context layer for low-resource languages."
        url="/"
        structuredData={[websiteStructuredData, organizationStructuredData]}
      />

      {/* 1. Hero Section */}
      <section className="mx-auto max-w-6xl px-5 pt-8 pb-16 md:px-8 md:pt-14 md:pb-24 lg:min-h-[calc(100vh-140px)] lg:flex lg:flex-col lg:justify-center">
        <div className="grid items-center gap-12 lg:gap-16 lg:grid-cols-2">
          <div className="lg:pr-4">
            <div className="gsap-hero-badge inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/70 backdrop-blur-xl mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--utu-gold))]" />
              AI & Systems Technology Lab
            </div>
            <h1 className="gsap-hero-title text-balance text-[2.25rem] font-bold leading-[1.05] tracking-tight text-white sm:text-[3rem] md:text-[3.5rem] lg:text-[4rem]">
              Sovereign AI.
              <br />
              Custom Systems.
              <br />
              Linguistic Context.
            </h1>
            <p className="gsap-hero-desc mt-5 max-w-xl text-pretty text-sm leading-relaxed text-white/70 md:text-base">
              We design and fine-tune private AI models that run fully on client hardware, engineer robust full-stack software systems, and deploy localized Context-as-a-Service infrastructure.
            </p>
            <div className="gsap-hero-cta mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <GoldPillButton className="h-11 px-6 text-sm">
                  Book Architecture Audit
                  <ChevronRight className="h-4 w-4" />
                </GoldPillButton>
              </a>
              <OutlinePillButton
                onClick={() => setWaitlistOpen(true)}
                className="h-11 px-6 text-sm"
              >
                Request MCP Access
                <ChevronRight className="h-4 w-4" />
              </OutlinePillButton>
            </div>
          </div>
          <div className="gsap-hero-orb relative flex items-center justify-center">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(252,211,77,0.15),transparent_70%)] blur-2xl" />
            <FingerprintOrb className="relative z-10" />
          </div>
        </div>
      </section>

      {/* 2. Service Grid Bento Section */}
      <section id="services" className="gsap-reveal mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24 border-t border-white/5">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <div className="text-xs font-semibold tracking-[0.24em] text-[hsl(var(--utu-gold))] uppercase">
            Service Verticals
          </div>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-5xl">
            Our Core Capabilities
          </h2>
          <p className="mt-4 text-sm text-white/60">
            We bypass vendor lock-in and security compromises by building dedicated tech solutions.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {SERVICE_CARDS.map((service) => {
            const IconComponent = serviceIcons[service.id] || Layers;
            return (
              <GlassCard
                key={service.id}
                className="p-8 flex flex-col justify-between h-full border border-white/10 hover:border-amber-500/20 transition-all duration-300 group"
              >
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <IconComponent className="h-8 w-8 text-[hsl(var(--utu-gold))]" />
                    <span className="text-[10px] text-white/40 tracking-wider font-semibold uppercase">
                      {service.subtitle}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[hsl(var(--utu-gold))] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm text-white/65 leading-relaxed mb-6">
                    {service.description}
                  </p>
                </div>
                <div>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {service.tags.map((tag) => (
                      <Badge
                        key={tag}
                        className="border-white/10 bg-white/5 text-white/70 backdrop-blur-xl text-[10px] font-normal"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                  <Link
                    to={service.href}
                    className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--utu-gold))] font-semibold hover:underline"
                  >
                    Explore Service Details
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </GlassCard>
            );
          })}
        </div>
      </section>

      {/* 3. How We Work (Audit -> Sprint -> Retainer) */}
      <section id="how-we-work" className="gsap-reveal mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24 border-t border-white/5">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <div className="text-xs font-semibold tracking-[0.24em] text-[hsl(var(--utu-gold))] uppercase">
            Process Model
          </div>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-5xl">
            How We Engage
          </h2>
          <p className="mt-4 text-sm text-white/60">
            We follow a structured deployment model designed for system reliability and performance scaling.
          </p>
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          {ENGAGEMENT_TIERS.map((tier, idx) => (
            <GlassCard key={tier.id} className="p-8 border border-white/10 flex flex-col justify-between h-full hover:border-amber-500/20 transition-all duration-300">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-2xl font-bold text-[hsl(var(--utu-gold))]">0{idx + 1}</span>
                  <span className="text-[10px] text-white/55 bg-white/5 border border-white/10 px-2 py-0.5 rounded uppercase font-semibold">{tier.duration}</span>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{tier.name}</h3>
                <p className="text-xs text-white/55 font-semibold tracking-wider mb-4">{tier.priceText}</p>
                <p className="text-sm text-white/65 leading-relaxed mb-6">{tier.description}</p>
              </div>
              <Link to="/engage/tiers" className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--utu-gold))] font-semibold hover:underline mt-4">
                View Deliverables Roadmap
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* 4. Flagship: UTU MCP Section & Interactive Diagram */}
      <section id="mcp" className="gsap-reveal mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24 border-t border-white/5">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] items-center">
          <div>
            <div className="text-xs font-semibold tracking-[0.24em] text-[hsl(var(--utu-gold))] uppercase mb-3">
              Flagship Innovation
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-white md:text-5xl mb-6">
              UTU MCP Server
            </h2>
            <p className="text-sm text-white/70 leading-relaxed mb-6">
              The Model Context Protocol (MCP) acts as a dynamic context layer, grounding standard LLMs in regional cultural memory and Agglutinative Bantu dialect structures. Bypassing heavy context window bloat, the server injects versioned local knowledge nodes in real-time.
            </p>

            {/* Hard Metrics */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="border-l border-amber-500/30 pl-4 py-1">
                <div className="text-2xl md:text-3xl font-bold text-[hsl(var(--utu-gold))]">61+</div>
                <div className="text-[10px] text-white/50 uppercase tracking-wider mt-1">Dialects</div>
              </div>
              <div className="border-l border-amber-500/30 pl-4 py-1">
                <div className="text-2xl md:text-3xl font-bold text-[hsl(var(--utu-gold))]">&lt;50ms</div>
                <div className="text-[10px] text-white/50 uppercase tracking-wider mt-1">Latency</div>
              </div>
              <div className="border-l border-amber-500/30 pl-4 py-1">
                <div className="text-2xl md:text-3xl font-bold text-[hsl(var(--utu-gold))]">50K+</div>
                <div className="text-[10px] text-white/50 uppercase tracking-wider mt-1">Daily Requests</div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <OutlinePillButton onClick={() => setWaitlistOpen(true)} className="h-10 px-5 text-xs">
                Request API Access
              </OutlinePillButton>
              <Link to="/mcp/playground">
                <OutlinePillButton className="h-10 px-5 text-xs">
                  Open Interactive Playground
                  <Play className="h-3 w-3 ml-1.5 fill-current" />
                </OutlinePillButton>
              </Link>
            </div>
          </div>

          {/* Interactive Pipeline Diagram */}
          <GlassCard className="p-6 md:p-8">
            <h4 className="text-xs font-bold text-white/55 tracking-wider uppercase mb-6 flex justify-between items-center">
              <span>Linguistic Context Pipeline</span>
              <span className="text-[10px] text-[hsl(var(--utu-gold))] font-semibold bg-amber-500/5 px-2 py-0.5 rounded border border-amber-500/10 animate-pulse">
                Live Simulation
              </span>
            </h4>

            {/* Stepper Visuals */}
            <div className="space-y-6">
              {/* Step 1: Input */}
              <div className={`p-4 rounded-xl border transition-all duration-300 ${
                pipelineStep === 0 ? "border-[hsl(var(--utu-gold))]/40 bg-amber-500/5" : "border-white/5 bg-white/[0.01] opacity-60"
              }`}>
                <div className="flex items-center justify-between text-xs font-bold mb-1">
                  <span className="text-white">01. Request Input Ingestion</span>
                  <span className="text-white/40">Raw Input</span>
                </div>
                <div className="font-mono text-xs text-white/80">“Form ni gani leo wadau?”</div>
              </div>

              {/* Step 2: Segmenter */}
              <div className={`p-4 rounded-xl border transition-all duration-300 ${
                pipelineStep === 1 ? "border-[hsl(var(--utu-gold))]/40 bg-amber-500/5" : "border-white/5 bg-white/[0.01] opacity-60"
              }`}>
                <div className="flex items-center justify-between text-xs font-bold mb-1">
                  <span className="text-white">02. Morphological Tokenizer</span>
                  <span className="text-emerald-400">Agglutinative Split</span>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {["Form", " ni", " gani", " leo", " wad", "au"].map((tok, i) => (
                    <span key={i} className="text-[9px] font-mono px-1.5 py-0.5 bg-emerald-400/10 border border-emerald-400/20 rounded text-emerald-200">
                      {tok}
                    </span>
                  ))}
                </div>
              </div>

              {/* Step 3: Context nodes */}
              <div className={`p-4 rounded-xl border transition-all duration-300 ${
                pipelineStep === 2 ? "border-[hsl(var(--utu-gold))]/40 bg-amber-500/5" : "border-white/5 bg-white/[0.01] opacity-60"
              }`}>
                <div className="flex items-center justify-between text-xs font-bold mb-1">
                  <span className="text-white">03. Context Memory Injection</span>
                  <span className="text-amber-400">Vector Search Match</span>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <span className="text-[9px] font-mono px-1.5 py-0.5 bg-amber-400/10 border border-amber-400/20 rounded text-amber-200">
                    ID: sheng_slang_greeting
                  </span>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 bg-amber-400/10 border border-amber-400/20 rounded text-amber-200">
                    Locality: Nairobi-Urban
                  </span>
                </div>
              </div>

              {/* Step 4: Stabilized Prompt */}
              <div className={`p-4 rounded-xl border transition-all duration-300 ${
                pipelineStep === 3 ? "border-[hsl(var(--utu-gold))]/40 bg-amber-500/5" : "border-white/5 bg-white/[0.01] opacity-60"
              }`}>
                <div className="flex items-center justify-between text-xs font-bold mb-1">
                  <span className="text-white">04. Stabilized Prompt Output</span>
                  <span className="text-blue-400">LLM Forwarding</span>
                </div>
                <p className="text-[10px] text-white/70 leading-relaxed font-sans">
                  "Context: Sheng greeting, meaning: 'What is the plan today, guys?'... Prompt: Form ni gani leo wadau?"
                </p>
              </div>
            </div>
          </GlassCard>
        </div>
      </section>

      {/* 5. Selected Work - Deep Case Studies */}
      <section id="case-studies" className="gsap-reveal mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24 border-t border-white/5">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <div className="text-xs font-semibold tracking-[0.24em] text-[hsl(var(--utu-gold))] uppercase">
            Selected Work
          </div>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-5xl">
            Case Studies
          </h2>
          <p className="mt-4 text-sm text-white/60">
            A technical review of systems deployed in production environments.
          </p>
        </div>

        {/* Case Study Tab Selectors */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {CASE_STUDIES.map((study) => (
            <OutlinePillButton
              key={study.id}
              onClick={() => setActiveCaseStudyId(study.id)}
              className={`h-9 px-4 text-xs font-semibold ${
                activeCaseStudyId === study.id
                  ? "border-[hsl(var(--utu-gold))] bg-amber-500/10 text-[hsl(var(--utu-gold))]"
                  : "border-white/10"
              }`}
            >
              {study.headline}
            </OutlinePillButton>
          ))}
        </div>

        {/* Selected Case Study Detail Display */}
        {CASE_STUDIES.map((study) => {
          if (study.id !== activeCaseStudyId) return null;
          return (
            <GlassCard key={study.id} className="p-8 md:p-12 border border-white/10">
              <div className="grid gap-10 lg:grid-cols-[1fr_1.5fr] items-start">
                {/* Side info & metrics */}
                <div className="space-y-6">
                  <div>
                    {study.hasMcpBadge && (
                      <Badge className="bg-[hsl(var(--utu-gold))]/10 border border-[hsl(var(--utu-gold))]/25 text-[hsl(var(--utu-gold))] text-[10px] font-semibold tracking-wider uppercase mb-3">
                        {study.mcpBadgeText}
                      </Badge>
                    )}
                    <h3 className="text-3xl font-bold text-white mb-2">{study.headline}</h3>
                    <p className="text-sm text-white/50">{study.subheadline}</p>
                  </div>

                  {/* Impact metrics grid */}
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/10">
                    {(study.results || []).map((metric) => (
                      <div key={metric.label}>
                        <div className="text-2xl font-bold text-[hsl(var(--utu-gold))]">{metric.metric}</div>
                        <div className="text-[10px] text-white/55 mt-1 leading-tight">{metric.label}</div>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-1.5">
                    {study.tags.map((t) => (
                      <Badge key={t} className="bg-white/5 border border-white/10 text-white/70 text-[9px]">
                        {t}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Technical detailed columns */}
                <div className="space-y-6 border-t lg:border-t-0 lg:border-l border-white/10 pt-6 lg:pt-0 lg:pl-10">
                  <div>
                    <h4 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-2">Challenge</h4>
                    <p className="text-sm text-white/75 leading-relaxed">{study.challenge}</p>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-2">Solution & Architecture</h4>
                    <p className="text-sm text-white/75 leading-relaxed">{study.solution}</p>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-2">Tech Stack Rationale</h4>
                    <p className="text-sm text-white/75 leading-relaxed font-sans">{study.stackReasoning}</p>
                  </div>

                  <div>
                    <h4 className="text-xs font-bold text-white/50 uppercase tracking-wider mb-2">What We'd Do Differently</h4>
                    <p className="text-sm text-white/75 leading-relaxed">{study.lessons}</p>
                  </div>
                </div>
              </div>
            </GlassCard>
          );
        })}
      </section>

      {/* 6. Insights Section - Technical articles list preview */}
      <section className="gsap-reveal mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24 border-t border-white/5">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12">
          <div>
            <div className="text-xs font-semibold tracking-[0.24em] text-[hsl(var(--utu-gold))] uppercase mb-3">
              Research & Notebooks
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-white md:text-5xl">
              Latest Insights
            </h2>
          </div>
          <Link to="/insights" className="text-xs font-semibold text-[hsl(var(--utu-gold))] flex items-center gap-1 hover:underline mt-4 md:mt-0 shrink-0">
            Open All Publications
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {INSIGHTS_ARTICLES.slice(0, 3).map((art) => (
            <GlassCard key={art.id} className="p-6 flex flex-col justify-between h-full border border-white/10 hover:border-amber-500/20 transition-all duration-300">
              <div>
                <div className="flex items-center gap-3 text-[10px] text-white/45 mb-3 font-semibold">
                  <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {art.readTime}
                  </span>
                  <span>&bull;</span>
                  <span>{art.date}</span>
                </div>
                <h4 className="text-lg font-bold text-white mb-2 line-clamp-2">{art.title}</h4>
                <p className="text-xs text-white/60 leading-relaxed mb-4 line-clamp-3">{art.summary}</p>
              </div>
              <Link
                to="/insights"
                className="text-xs text-[hsl(var(--utu-gold))] font-semibold flex items-center gap-1 hover:underline mt-4 pt-4 border-t border-white/5"
              >
                Read Paper
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            </GlassCard>
          ))}
        </div>
      </section>

      {/* 7. Stack & Philosophy */}
      <section className="gsap-reveal mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24 border-t border-white/5">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <div className="text-xs font-semibold tracking-[0.24em] text-[hsl(var(--utu-gold))] uppercase">
            Development Stack
          </div>
          <h2 className="mt-3 text-3xl font-bold tracking-tight text-white md:text-5xl">
            Sovereign Technology Rationale
          </h2>
          <p className="mt-4 text-sm text-white/60">
            Why we choose specific core architectures for enterprise data compliance.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 mb-10">
          {TECH_STACK.slice(0, 6).map((tech) => (
            <GlassCard key={tech.name} className="p-6 border border-white/10 hover:border-amber-500/20 transition-all duration-300">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-lg font-bold text-white">{tech.name}</h4>
                <span className="text-[9px] text-white/50 bg-white/5 px-2 py-0.5 rounded font-mono uppercase border border-white/10">
                  {tech.category}
                </span>
              </div>
              <p className="text-xs text-white/65 leading-relaxed">{tech.reason}</p>
            </GlassCard>
          ))}
        </div>
        <div className="text-center">
          <Link to="/about" className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--utu-gold))] font-semibold hover:underline">
            Read Complete Stack & About Philosophy
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* 8. Dual CTA & Contact */}
      <section id="contact" className="gsap-reveal mx-auto max-w-6xl px-5 py-16 md:px-8 md:py-24 border-t border-white/5">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] items-start">
          <div>
            <div className="text-xs font-semibold tracking-[0.24em] text-[hsl(var(--utu-gold))] uppercase mb-3">
              Start a Project
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-white md:text-5xl mb-6">
              Discuss Your Infrastructure
            </h2>
            <p className="text-sm text-white/65 leading-relaxed mb-6">
              Schedule an architecture audit or get in touch for custom system design. Our senior engineers review requests and reply within 24 hours.
            </p>

            {/* Email link */}
            <p className="text-xs text-white/50">
              Direct Inquiries:{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[hsl(var(--utu-gold))] font-semibold hover:underline">
                {CONTACT_EMAIL}
              </a>
            </p>

            <div className="h-px bg-white/10 my-8" />

            <div className="flex flex-col sm:flex-row gap-3">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="shrink-0">
                <GoldPillButton className="h-11 px-6 text-sm">
                  Book Architecture Audit
                </GoldPillButton>
              </a>
              <OutlinePillButton onClick={() => setWaitlistOpen(true)} className="h-11 px-6 text-sm">
                Request MCP Access
              </OutlinePillButton>
            </div>
          </div>

          <GlassCard className="p-8">
            <h4 className="text-xs font-bold text-white/55 tracking-wider uppercase mb-6">
              Audit Consulting Ingestion
            </h4>
            <form onSubmit={submitLead} className="space-y-4">
              <Input
                value={lead.name}
                onChange={(e) => setLead((p) => ({ ...p, name: e.target.value }))}
                placeholder="Contact Name"
                required
                className="h-11 border-white/10 bg-black/25 text-white placeholder:text-white/40 focus-visible:ring-[hsl(var(--utu-gold))]/40"
              />
              <Input
                value={lead.email}
                onChange={(e) => setLead((p) => ({ ...p, email: e.target.value }))}
                placeholder="Corporate Email"
                type="email"
                required
                className="h-11 border-white/10 bg-black/25 text-white placeholder:text-white/40 focus-visible:ring-[hsl(var(--utu-gold))]/40"
              />
              <Input
                value={lead.company}
                onChange={(e) => setLead((p) => ({ ...p, company: e.target.value }))}
                placeholder="Organization Name"
                className="h-11 border-white/10 bg-black/25 text-white placeholder:text-white/40 focus-visible:ring-[hsl(var(--utu-gold))]/40"
              />
              <Textarea
                value={lead.message}
                onChange={(e) => setLead((p) => ({ ...p, message: e.target.value }))}
                placeholder="Detail system specifications or integration challenges..."
                rows={4}
                required
                className="border-white/10 bg-black/25 text-white placeholder:text-white/40 resize-none focus-visible:ring-[hsl(var(--utu-gold))]/40"
              />
              <GoldPillButton type="submit" disabled={submitting} className="w-full h-11 justify-center">
                {submitting ? "Ingesting..." : "Send Submission"}
              </GoldPillButton>
            </form>
          </GlassCard>
        </div>
      </section>

      {/* Waitlist Modal */}
      {waitlistOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-md">
          <GlassCard className="w-full max-w-md p-6 md:p-8 border border-white/10 shadow-2xl relative">
            <button
              onClick={() => setWaitlistOpen(false)}
              className="absolute right-4 top-4 p-2 text-white/70 hover:text-white rounded-full bg-white/5 border border-white/10"
              aria-label="Close waitlist modal"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="mb-6">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/10 border border-amber-500/20 text-[hsl(var(--utu-gold))] mb-3">
                <Cpu className="h-5 w-5" />
              </div>
              <h3 className="text-xl font-bold text-white">Request UTU MCP Access</h3>
              <p className="text-xs text-white/55 mt-1">
                Enter your details to join the developer waitlist for the cloud-hosted context layer.
              </p>
            </div>

            <form onSubmit={handleWaitlistSubmit} className="space-y-4">
              <div>
                <label className="text-[10px] font-bold text-white/55 uppercase tracking-wider block mb-1">Name</label>
                <Input
                  value={waitlistLead.name}
                  onChange={(e) => setWaitlistLead((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Developer Name"
                  required
                  className="h-10 border-white/10 bg-black/25 text-white placeholder:text-white/40 focus-visible:ring-[hsl(var(--utu-gold))]/40"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-white/55 uppercase tracking-wider block mb-1">Email</label>
                <Input
                  value={waitlistLead.email}
                  onChange={(e) => setWaitlistLead((p) => ({ ...p, email: e.target.value }))}
                  placeholder="name@domain.com"
                  type="email"
                  required
                  className="h-10 border-white/10 bg-black/25 text-white placeholder:text-white/40 focus-visible:ring-[hsl(var(--utu-gold))]/40"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-white/55 uppercase tracking-wider block mb-1">Role / Affiliation</label>
                <Input
                  value={waitlistLead.role}
                  onChange={(e) => setWaitlistLead((p) => ({ ...p, role: e.target.value }))}
                  placeholder="e.g. NLP Researcher, ML Engineer"
                  required
                  className="h-10 border-white/10 bg-black/25 text-white placeholder:text-white/40 focus-visible:ring-[hsl(var(--utu-gold))]/40"
                />
              </div>

              <div>
                <label className="text-[10px] font-bold text-white/55 uppercase tracking-wider block mb-1">Linguistic Use Case</label>
                <Textarea
                  value={waitlistLead.use_case}
                  onChange={(e) => setWaitlistLead((p) => ({ ...p, use_case: e.target.value }))}
                  placeholder="Briefly describe what regional languages or dialects you want to segment..."
                  rows={3}
                  required
                  className="border-white/10 bg-black/25 text-white placeholder:text-white/40 resize-none focus-visible:ring-[hsl(var(--utu-gold))]/40"
                />
              </div>

              <GoldPillButton
                type="submit"
                disabled={waitlistSubmitting}
                className="w-full h-11 justify-center"
              >
                {waitlistSubmitting ? "Submitting..." : "Join Developer Waitlist"}
              </GoldPillButton>
            </form>
          </GlassCard>
        </div>
      )}
    </Layout>
  );
}
